import { useEffect, useRef } from "react";
import { useLocation } from "@/lib/router-compat";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Fires a single GA4 page_view per navigation, AFTER the page title has
 * stabilised. Watches <title> mutations and debounces.
 *
 * Why: We disable GA's automatic page_view (send_page_view: false) so that
 * transient titles like "Article Not Found" set by a still-loading React
 * Query don't get reported for valid URLs. This hook flushes the page_view
 * once the title has stopped changing (debounce 800ms) or after a 3s
 * fallback.
 */
export const GAPageView = () => {
  const { pathname, search } = useLocation();
  const lastSentRef = useRef<string>("");

  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    if (typeof window === "undefined") return;

    const url = pathname + search;
    let debounceTimer: number | undefined;
    let fallbackTimer: number | undefined;
    let sent = false;

    const send = () => {
      if (sent) return;
      const title = document.title || "";
      const key = `${url}::${title}`;
      if (lastSentRef.current === key) return;
      lastSentRef.current = key;
      sent = true;
      window.gtag?.("event", "page_view", {
        page_title: title,
        page_location: window.location.href,
        page_path: url,
      });
    };

    const titleEl = document.querySelector("title");
    const scheduleSend = () => {
      if (debounceTimer) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(send, 800);
    };

    let observer: MutationObserver | undefined;
    if (titleEl) {
      observer = new MutationObserver(scheduleSend);
      observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
    }
    scheduleSend();
    fallbackTimer = window.setTimeout(send, 3000);

    return () => {
      if (debounceTimer) window.clearTimeout(debounceTimer);
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
      observer?.disconnect();
    };
  }, [pathname, search]);

  return null;
};
